/** @jsxImportSource @emotion/react */
import React, { PropsWithChildren, useRef } from 'react';
import { jsx, css } from '@emotion/react';
import * as yup from 'yup';
import IBAN from 'iban';
import { FormattedMessage, useIntl } from 'components/i18n';
import { RadioGroup, Radio } from '@atlaskit/radio';
import { CountrySelect } from 'components/select';
import { colors } from '@atlaskit/theme';
import { ValidMessage } from '@atlaskit/form';
import Spinner from '@atlaskit/spinner';
import DropdownMenu from '@atlaskit/dropdown-menu';
import { Customer } from './types';
import Textfield from '../../components/textfield';
import { customerType } from './messages';
import { Grid, GridColumn } from '../../components/page';
import CompanySuggest from '../../components/autosuggest/CompanySuggest';
import { CompanyAvatar } from '../../components/avatar';
import validateVatIdentificationNumber from '../../components/form/validateVatIdentificationNumber';
import {
  Field,
  Form,
  validate,
  VatIdentificationNumberInput,
} from '../../components/form';
import { isEU, isNL } from '../../libs/countries';

const defaultProps = {};

type Props = {
  onSubmit: (customer: Customer) => void;
  initialValues: Customer;
} & typeof defaultProps;

const vatIdentificationNumberRequired = (countryCode: string) =>
  isEU(countryCode) && !isNL(countryCode);

const schema = yup.object({
  address: yup.object({
    country: yup.string().required(),
  }).required()
  // type: yup.string().required('Required'),
  // bankAccountNumber: yup
  //   .string()
  //   .test(
  //     'is-iban',
  //     'Must be a valid IBAN number',
  //     (value) => !value || IBAN.isValid(value)
  //   ),
  // email: yup.string().email('Must be a valid e-mail address'),
});

const customerTypeOptions = [
  {
    name: 'type',
    value: 'business',
    label: <FormattedMessage {...customerType.business} />,
  },
  {
    name: 'type',
    value: 'private',
    label: <FormattedMessage {...customerType.private} />,
  },

];
//
// const Field = (props: React.ComponentProps<typeof BaseField>) => (
//   <BaseField
//     // isCompact
//     {...props} />
// );

const vatValidate = async (value: any, allValues: any) => {
  const country = (allValues as Customer)?.address?.country || '';
  if (vatIdentificationNumberRequired(country) && !value) {
    return 'Required';
  }
  // if (isEU(country)) {
  //   return validateVatIdentificationNumber(value);
  // }
  return null;
};

const CustomerForm = ({
  onSubmit,
  initialValues,
}: PropsWithChildren<Props>) => {
  const isNew = (initialValues as Customer & { isNew: boolean })?.isNew;
  const ref = useRef<HTMLFormElement | null>(null);
  const intl = useIntl();
  return (
    <Form<Customer>
      onSubmit={(values) => onSubmit(values)}
      initialValues={initialValues}
      validate={validate(schema)}
    >
      {({ handleSubmit, values, form, errors }) => {
        const type = values?.type || 'business';
        const isBusiness = type === 'business';
        return (
          <form onSubmit={handleSubmit} ref={ref} style={{width: "100%"}}>
            <button id="customerForm" type="submit" style={{display: 'none'}}>Submit</button>
            <Field name="type" label="Type">
              {({ input }) => (
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      margin-left: -5px;
                    `}
                  >
                    <RadioGroup options={customerTypeOptions} {...input} />
                  </div>
                </div>
              )}
            </Field>
            {isBusiness && (
              <Field<string>
                label={
                  <FormattedMessage
                    id="sale.CustomerForm.tradeName"
                    defaultMessage="Trade name"
                  />
                }
                name="tradeName"
              >
                {({ input }) => {
                  return (
                    <CompanySuggest
                      {...input}
                      onSelect={(company) => {
                        console.log('company select', company);
                        form.initialize((values) => {
                          return { ...values, ...company };
                        });
                      }}
                      elemBeforeInput={
                        <div
                          css={css`
                            padding-left: 0.5rem;
                          `}
                        >
                          <CompanyAvatar
                            size="small"
                            website={values?.website}
                          />
                        </div>
                      }
                      autoFocus={isNew}
                    />
                  );
                }}
              </Field>
            )}
            <h4>
              <FormattedMessage
                id="sale.CustomerForm.contactPerson"
                defaultMessage="Contact person"
              />
            </h4>
            <Field
              label={
                <FormattedMessage
                  id="sale.CustomerForm.name"
                  defaultMessage="Name"
                />
              }
              name="contactName"
            >
              <Textfield />
            </Field>
            <Grid spacing="cosy">
              <GridColumn>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.CustomerForm.email"
                      defaultMessage="Email"
                    />
                  }
                  name="email"
                >
                  <Textfield />
                </Field>
              </GridColumn>
              <GridColumn>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.CustomerForm.phone"
                      defaultMessage="Phone"
                    />
                  }
                  name="phone"
                >
                  <Textfield />
                </Field>
              </GridColumn>
            </Grid>
            <h4>
              <FormattedMessage
                id="sale.CustomerForm.address.address"
                defaultMessage="Address"
              />
            </h4>
            <Field
              label={
                <FormattedMessage
                  id="sale.CustomerForm.address.line1"
                  defaultMessage="Line 1"
                />
              }
              name="address.line1"
            >
              <Textfield />
            </Field>
            <Field
              label={
                <FormattedMessage
                  id="sale.CustomerForm.address.line2"
                  defaultMessage="Line 2"
                />
              }
              name="address.line2"
            >
              <Textfield />
            </Field>

            <Grid spacing="cosy">
              <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.CustomerForm.address.postalCode"
                      defaultMessage="Postal code"
                    />
                  }
                  name="address.postalCode"
                >
                  <Textfield />
                </Field>
              </GridColumn>
              <GridColumn medium={4}>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.CustomerForm.address.city"
                      defaultMessage="City"
                    />
                  }
                  name="address.city"
                >
                  <Textfield />
                </Field>
              </GridColumn>
              <GridColumn medium={4}>
                <Field
                  label={(
                    <FormattedMessage
                      id="sale.CustomerForm.address.country"
                      defaultMessage="Country"
                    />
                  )}
                  name="address.country"
                >
                  <CountrySelect />
                </Field>
              </GridColumn>
            </Grid>
            <h4>
              <FormattedMessage
                id="sale.CustomerForm.other"
                defaultMessage="Other"
              />
            </h4>
            {isBusiness && (
              <React.Fragment>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.CustomerForm.website"
                      defaultMessage="Website"
                    />
                  }
                  width="medium"
                  name="website"
                >
                  <Textfield />
                </Field>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.CustomerForm.companyRegistrationNumber"
                      defaultMessage="Company registration number"
                    />
                  }
                  width="medium"
                  name="companyRegistrationNumber"
                >
                  <Textfield />
                </Field>
                <Field
                  label={
                    <FormattedMessage
                      id="sale.CustomerForm.vatNumber"
                      defaultMessage="VAT ID number"
                    />
                  }
                  validate={vatValidate}
                  name="vatIdentificationNumber"
                  placeholder={
                    intl.formatMessage({
                      id:"sale.CustomerForm.vatNumberPlaceholder",
                      defaultMessage:"Country code + number"})
                  }
                  width="medium"
                  component={VatIdentificationNumberInput}
                />
                <Field
                  label={
                    <FormattedMessage
                      id="sale.CustomerForm.bankAccountNumber"
                      defaultMessage="Account number"
                    />
                  }
                  width="medium"
                  placeholder="IBAN"
                  name="bankAccountNumber"
                >
                  <Textfield />
                </Field>
              </React.Fragment>
            )}
            <Field
              label={
                <FormattedMessage
                  id="sale.CustomerForm.paymentTerm"
                  defaultMessage="Payment term"
                />
              }
              name="paymentTerm"
              component={Textfield}
              width={90}
              placeholder="14"
              type="number"
              elemAfterInput={
                <div
                  css={css`
                    padding-right: 0.5rem;
                    color: ${colors.subtleText()};
                  `}
                >
                  <FormattedMessage
                    id="sale.CustomerForm.days"
                    defaultMessage="days"
                  />
                </div>
              }
            />

          </form>
        );
      }}
    </Form>
  );
};

CustomerForm.defaultProps = defaultProps;

export default CustomerForm;
